import { AppError, IdSchema } from "@hisani/core";
import { z } from "zod";
import { AUTH_ERROR_CODES, } from "./service.js";
export const PROFILE_PICTURE_MAX_BYTES = 5 * 1024 * 1024;
export const ProfilePictureContentTypeSchema = z.enum([
    "image/jpeg",
    "image/png",
    "image/webp",
]);
const UploadSchema = z.object({
    id: IdSchema,
    filename: z.string().trim().min(1).max(180),
    contentType: ProfilePictureContentTypeSchema,
    contentLength: z.number().int().positive().max(PROFILE_PICTURE_MAX_BYTES),
});
const CommitSchema = UploadSchema.omit({ filename: true }).extend({
    objectKey: z.string().trim().min(1).max(500),
});
export async function beginProfilePictureUpload(principal, input, dependencies) {
    const parsed = UploadSchema.parse(input);
    const pictures = requireProfilePictures(dependencies);
    const safeFilename = parsed.filename.replace(/[^A-Za-z0-9._-]/gu, "-");
    const objectKey = `private/profile-pictures/${principal.account.id}/${parsed.id}-${safeFilename}`;
    const upload = await pictures.storage.createUpload({
        objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
        expiresInSeconds: 300,
    });
    return { id: parsed.id, objectKey, uploadUrl: upload.uploadUrl };
}
export async function saveProfilePicture(principal, input, dependencies) {
    const parsed = CommitSchema.parse(input);
    const expectedPrefix = `private/profile-pictures/${principal.account.id}/${parsed.id}-`;
    if (!parsed.objectKey.startsWith(expectedPrefix)) {
        throw new AppError({
            code: AUTH_ERROR_CODES.forbidden,
            message: "The uploaded profile picture does not belong to this account.",
        });
    }
    const pictures = requireProfilePictures(dependencies);
    const uploaded = await pictures.storage.assertUploaded({
        objectKey: parsed.objectKey,
        contentType: parsed.contentType,
        contentLength: parsed.contentLength,
    });
    await pictures.storage.claimUploaded({
        objectKey: parsed.objectKey,
        versionId: uploaded.versionId,
    });
    const updatedAt = dependencies.clock.now();
    let previous;
    try {
        previous = await pictures.repository.replace({
            horsemanId: principal.account.id,
            picture: {
                objectKey: parsed.objectKey,
                versionId: uploaded.versionId,
                contentType: parsed.contentType,
                contentLength: parsed.contentLength,
                updatedAt,
            },
        });
    }
    catch (error) {
        try {
            await pictures.storage.deleteObject({
                objectKey: parsed.objectKey,
                versionId: uploaded.versionId,
            });
        }
        catch (cleanupError) {
            throw new AppError({
                code: AUTH_ERROR_CODES.profilePictureUnavailable,
                message: "The profile picture could not be saved or cleaned up.",
                cause: new AggregateError([error, cleanupError]),
            });
        }
        throw error;
    }
    if (previous !== null &&
        (previous.objectKey !== parsed.objectKey ||
            previous.versionId !== uploaded.versionId)) {
        await pictures.storage.deleteObject(previous);
    }
    return updatedAt;
}
export async function removeProfilePicture(principal, dependencies) {
    const pictures = requireProfilePictures(dependencies);
    const previous = await pictures.repository.remove(principal.account.id);
    if (previous !== null)
        await pictures.storage.deleteObject(previous);
}
export async function getProfilePictureDownloadUrl(principal, dependencies) {
    const pictures = requireProfilePictures(dependencies);
    const picture = await pictures.repository.get(principal.account.id);
    if (picture === null)
        return null;
    return pictures.storage.createDownloadUrl({
        objectKey: picture.objectKey,
        versionId: picture.versionId,
        contentType: picture.contentType,
        expiresInSeconds: 300,
    });
}
function requireProfilePictures(dependencies) {
    if (dependencies.profilePictures === undefined) {
        throw new AppError({
            code: AUTH_ERROR_CODES.profilePictureUnavailable,
            message: "Profile pictures are temporarily unavailable.",
        });
    }
    return dependencies.profilePictures;
}
